import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import { useAccess } from '../../../../hooks/useAccess';
import { useApp } from '../../../../hooks/useApp';
import { isDigitalProductAdminUser } from '../../../../services/UserRole';
import { groupBy } from '../../../../utils/groupBy';
import { LoadingSpinnerPage } from '../../../LoadingSpinnerPage';
import { configuratorActions } from '../../useConfigurator/configuratorActions';
import { useConfigurator } from '../../useConfigurator/useConfigurator';
import { GroupItem } from './GroupItem';

type ProjectDto = SimpleProjectServiceAPI.ProjectDto;
type ConfigurationRequest = SimpleProjectServiceAPI.ConfigurationRequestDto;
type ConfigurationRequestDto = SimpleProjectServiceAPI.ConfigurationRequestDto;
const NO_REQUEST = 'no_request';

export interface ConfigurationWithRender {
  configuration: ConfigurationDto;
  render: ReactElement;
}

interface Props {
  configurations: ConfigurationWithRender[];
  isSelectionMode?: boolean;
  visible?: boolean;
  fullscreen?: boolean;
  indented?: boolean;
  nospace?: boolean;
  requests: ConfigurationRequest[];
  project?: ProjectDto;
  selectedConfigurationCode?: string;
  downloadGLB?: (configurations: ConfigurationDto[], projectCode: string) => void;
  downloadProjectPdf?: (
    configurations: ConfigurationDto[],
    project?: ProjectDto,
    request?: ConfigurationRequestDto,
  ) => void;
}

export const GroupView: React.FC<Props> = ({
  configurations,
  visible = true,
  isSelectionMode,
  fullscreen,
  indented,
  requests,
  project,
  nospace,
  selectedConfigurationCode,
  downloadGLB,
  downloadProjectPdf,
}) => {
  const { roles } = useApp();
  const { isSalesAccessingSameDistributorRequest } = useAccess();
  const [{ currentConfigurations, selectedRequestIds }, dispatch] = useConfigurator();
  const [groupedByRequest, setGroupedByRequest] = useState<Map<string, ConfigurationWithRender[]>>(
    new Map(),
  );
  const t = useTranslate('RequestGroupView');

  useEffect(() => {
    setGroupedByRequest(
      groupBy(
        configurations,
        ({ configuration }) => configuration.configurationRequestId || NO_REQUEST,
      ),
    );
  }, [configurations]);

  const handleSelect = useCallback(
    (request: SimpleProjectServiceAPI.ConfigurationRequestDto) => (value: boolean) => {
      const ids = value
        ? currentConfigurations
            .filter((item) => item.configurationRequestId === request.id)
            .map((item) => item.storeId)
        : [];
      dispatch(configuratorActions.setSelectedConfigurationIds(ids));
      dispatch(configuratorActions.setSelectedRequestIds(value ? [request.id] : []));
    },
    [currentConfigurations, dispatch],
  );

  const configurationsWithoutRequest = groupedByRequest.get(NO_REQUEST) || [];

  if (!requests) {
    return <LoadingSpinnerPage />;
  }

  return (
    <>
      {visible &&
        requests.length > 0 &&
        Array.from(groupedByRequest.keys())
          .filter((requestId) => requestId !== NO_REQUEST)
          .map((requestId) => {
            const request = requests.find((request) => request.id === requestId);
            const visibleForSales = isSalesAccessingSameDistributorRequest(request);
            const visibleForSelection =
              isSelectionMode &&
              !request?.sapOffer &&
              (isDigitalProductAdminUser(roles) || visibleForSales);
            const visibleForView = request && !isSelectionMode;
            const checked = selectedRequestIds.indexOf(requestId) > -1;
            const groupedConfigurations = groupedByRequest.get(requestId) || [];
            return (
              (visibleForSelection || visibleForView) && (
                <GroupItem
                  t={t}
                  key={requestId}
                  isSelectionMode={isSelectionMode}
                  request={request}
                  configurations={groupedByRequest.get(requestId) || []}
                  project={project}
                  fullscreen={fullscreen}
                  indented={indented}
                  nospace={nospace}
                  downloadGLB={downloadGLB}
                  downloadProjectPdf={downloadProjectPdf}
                  onSelect={handleSelect(request)}
                  checked={checked}
                  isAccordionOpened={groupedConfigurations.some(
                    (group) => group.configuration.code === selectedConfigurationCode,
                  )}
                />
              )
            );
          })}
      {configurationsWithoutRequest.length > 0 && requests.length > 0 && fullscreen && (
        <Separator />
      )}
      {configurationsWithoutRequest.length > 0 && (
        <NoRequestGroup fullscreen={fullscreen}>
          {configurationsWithoutRequest.map(({ render }) => render)}
        </NoRequestGroup>
      )}
    </>
  );
};

type ConfigurationDto = SimpleProjectServiceAPI.ConfigurationDto;

const NoRequestGroup = styled.div<{ fullscreen?: boolean }>`
  ${({ fullscreen, theme }) => `
    border: 1px solid ${theme.colors.layout40};
    border-left: ${fullscreen ? 'none' : `1px solid ${theme.colors.layout40}`};
    border-right: ${fullscreen ? 'none' : `1px solid ${theme.colors.layout40}`};
  `}
}`;

const Separator = styled.div``;
